import { useState } from 'react';
import { Link } from 'react-router-dom';

import AgentFace from '../../../common/components/agent-face/agent-face';
import { agentLook } from '../../../common/components/agent-face/looks';
import { formatAgo, formatTime } from '../../../core/utils/date';
import { useAgents } from '../contexts/live';

import '../styles/agents.css';

// Who is working right now, at the top of the screen because it is the first
// question anybody asks of a live board.
//
// An agent is not a session that connects, so there is nothing to be "online":
// it is a credential that acts. What the system does write is when each token
// was last used and which token holds each reservation, and between those two
// the question has a real answer — one that survives an agent whose process
// died, because what expires is the reservation, not the connection.
//
// A revoked token is not listed even if it was used ten minutes ago, and a
// reservation past its expiry is not work in progress: the next claim will take
// that task away without asking.
//
// PRIMERO EL RECUENTO, DESPUÉS LAS CARAS. Con treinta credenciales, la tira de
// tarjetas se comía la pantalla antes de llegar al pulso, que es lo que esta
// sección existe para mostrar. Así que arriba va cuántos hay de cada clase, y
// las tarjetas se dibujan SOLO de quien tiene una tarea en la mano — que son
// las que contestan «quién está trabajando». El resto se despliega a pedido.

const STATE_TEXT = {
    working: 'trabajando',
    expired: 'reserva vencida',
    idle:    'sin tarea',
};

export default function Agents({ projectId }) {
    const { agents, byKind, working, isReadable, countedAt, isLoading } = useAgents({ projectId });
    const [showAll, setShowAll] = useState(false);

    if (!isReadable) {
        return (
            <section className="agents">
                <p className="agents__empty">
                    Hace falta <code>tokens:admin</code> para ver quién está trabajando.
                </p>
            </section>
        );
    }

    const stalled = agents.filter(agent => agent.state === 'expired').length;
    // Con tarea en la mano: trabajando o con la reserva vencida. Las dos son
    // «esta credencial tiene algo tomado», y la vencida es la que hay que mirar.
    const busy = agents.filter(agent => agent.state !== 'idle');
    const idle = agents.length - busy.length;
    const shown = showAll ? agents : busy;

    return (
        <section className="agents">
            <div className="agents__banner">
                <div className="kpi">
                    <span className="kpi__value">
                        {isLoading ? '—' : working}
                        <span className="kpi__of">/{isLoading ? '—' : agents.length}</span>
                    </span>
                    <span className="kpi__label">agentes con una tarea en la mano</span>
                    <span className="kpi__foot">
                        {countedAt > 0 ? `contado a las ${formatTime(countedAt)}` : 'preguntando…'}
                        {/* A stalled reservation looks like work and is not, so it
                            gets counted where the number that matters is. */}
                        {stalled > 0 && ` · ${stalled} con la reserva vencida`}
                    </span>
                </div>

                {/* EL RECUENTO POR CLASE. Un esclavo toma tareas, un maestro
                    desglosa y revisa, un CLI solo lee: por eso la columna de
                    «trabajando» no existe para el CLI en vez de valer cero. */}
                <ul className="agents__kinds">
                    {byKind.map(kind => (
                        <li
                            key={kind.kind}
                            className={`agent-kind agent-kind--tone-${agentLook(kind.kind).tone}`}
                        >
                            <AgentFace kind={kind.kind} state="idle" size={32} title={kind.label} />
                            <span className="agent-kind__body">
                                <span className="agent-kind__count">{isLoading ? '—' : kind.total}</span>
                                <span className="agent-kind__label">{kind.label}</span>
                                <span className="agent-kind__state">
                                    {!kind.takesWork
                                        ? 'no toma tareas'
                                        : kind.total === 0
                                            ? 'ninguno activo'
                                            : `${kind.working} con tarea${kind.expired > 0 ? ` · ${kind.expired} vencida${kind.expired === 1 ? '' : 's'}` : ''}`}
                                </span>
                            </span>
                        </li>
                    ))}
                </ul>
            </div>

            {!isLoading && !agents.length && (
                <p className="agents__empty">
                    No hay ningún token de agente activo. Sin token no hay agente: el
                    sistema no conoce procesos, conoce credenciales.
                </p>
            )}

            {agents.length > 0 && (
                <div className="agents__roster">
                    <div className="agents__roster-head">
                        <span className="agents__roster-title">
                            {showAll
                                ? `Los ${agents.length} agentes`
                                : busy.length > 0
                                    ? `Con tarea en la mano · ${busy.length}`
                                    : 'Nadie tiene una tarea en la mano'}
                        </span>
                        {idle > 0 && (
                            <button
                                type="button"
                                className="agents__toggle"
                                onClick={() => setShowAll(!showAll)}
                                aria-expanded={showAll}
                            >
                                {showAll
                                    ? 'Ver solo los que tienen tarea'
                                    : `Ver todos (${idle} sin tarea)`}
                            </button>
                        )}
                    </div>

                    {shown.length > 0 && (
                        <ul className="agents__strip">
                            {shown.map(agent => (
                                <li
                                    key={agent.id}
                                    className={`agent-card agent-card--${agent.state} agent-card--tone-${agentLook(agent.kind).tone}`}
                                >
                                    <AgentFace state={agent.state} kind={agent.kind} size={44} title={agent.name} />

                                    <div className="agent-card__body">
                                        <span className="agent-card__name">
                                            {agent.name}
                                            <span className="agent-card__kind">{agent.kind}</span>
                                        </span>

                                        {agent.task
                                            ? (
                                                <Link
                                                    to={`/admin/tasks/${agent.task.id}`}
                                                    className="agent-card__task"
                                                    title={agent.task.title}
                                                >
                                                    <span className="agent-card__task-id">#{agent.task.id}</span>
                                                    <span className="agent-card__task-title">{agent.task.title}</span>
                                                </Link>
                                            )
                                            : <span className="agent-card__none">{STATE_TEXT[agent.state]}</span>}

                                        <span className="agent-card__meta">
                                            {agent.task && (
                                                <>
                                                    {agent.state === 'expired'
                                                        ? 'reserva vencida'
                                                        : `reserva hasta ${formatTime(agent.until)}`}
                                                    {' · '}
                                                </>
                                            )}
                                            visto {formatAgo(agent.lastUsedAt, countedAt || undefined)}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </section>
    );
}
